import type { PaymentMethodCreateParams } from '@stripe/stripe-js';

/** 支払い方法（`settlement_method`）に対応する日本語表示名 */
export const SETTLEMENT_METHOD_LABELS = {
  card: 'クレジットカード払い',
  invoice: '請求書払い',
};

/**
 * Stripe の card brand 値（`visa` / `mastercard` 等）に対応する表示名。
 *
 * 表記は **各ブランドが日本で最も通用するもの** に揃える（「英語で統一」ではない）。
 * ブランドロゴを出さない方針のため、テキストだけで自分のカードを識別してもらう必要があり、
 * カード券面・利用明細と一致する表記が照合しやすい。`unionpay` は「銀聯」の呼称が定着して
 * いるため漢字のまま。申込フォーム（fukuri-web-apps）側の同名定数と同じ表記に合わせている
 * （同じ企業が両方の画面を見るため、表記が食い違うと同じカードだと分からなくなる）。
 *
 * @see https://docs.stripe.com/api/cards/object#card_object-brand
 */
export const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  jcb: 'JCB',
  diners: 'Diners Club',
  discover: 'Discover',
  unionpay: '銀聯',
};

/**
 * 決済失敗の案内メール（dunning）のリンクに Stripe が自動で付与するクエリ。
 *
 * Stripe Dashboard の「決済手段の更新 → カスタムリンク」に設定しているのはクエリなしの
 * `/contract-info` だが、実際に配信されるメールのリンクには Stripe がこのクエリを足す。
 * 自社で付けている値ではないため、Stripe 側の仕様変更で消えうる前提で扱う
 * （クエリが無くても画面は通常どおり動く）。
 */
export const STRIPE_SOURCE_PARAM = {
  key: 'stripe_source',
  failedPayment: 'failed_payment',
} as const;

/**
 * 決済失敗の案内メール（dunning）から遷移してきたときに、お支払い方法セクションの先頭に出す案内。
 *
 * メールは「￥○○ の支払いを失敗しました」という文面で届くため、着地点でも失敗したことと
 * 次にやること（カードの確認）が分かるようにする。
 */
export const FAILED_PAYMENT_NOTICE =
  'お支払いに失敗しました。カード情報をご確認のうえ、必要に応じて変更してください。';

/** エラー文言 */
export const ERROR_MESSAGE = {
  /** 404: クレジットカード払いの契約ではない（Stripe Customer 未結線） */
  notCardPayment: 'クレジットカード払いのご契約ではないため、カード情報を変更できません。',
  /** 404 以外（502 等。Stripe 側のエラーを含む） */
  unexpected: 'カード情報の変更を開始できませんでした。時間をおいて再度お試しください。',
  /**
   * カード登録（elements.submit / confirmSetup）が失敗し、Stripe がエラーメッセージを返さなかった場合。
   *
   * カード不正・decline 等の想定内のエラーでは Stripe が日本語（locale: 'ja'）の
   * `error.message` を返すため、そちらをそのまま表示する。
   */
  cardRegistrationFailed: 'カード情報を登録できませんでした。時間をおいて再度お試しください。',
  /**
   * 既定カード情報の取得が 404 以外で失敗した場合（500 / タイムアウト / ネットワーク断）。
   *
   * 404（カード未登録）は正常系なのでこの文言は出さない。カード情報だけが取得できない状態で、
   * 他セクションの表示とカード変更（編集ボタン）は引き続き使えるため、再試行を促す文言に留める。
   */
  cardFetchFailed: 'カード情報を取得できませんでした。時間をおいて再度お試しください。',
} as const;

/**
 * カード登録成功時の案内文言
 *
 * 新カードの既定化は Stripe の `setup_intent.succeeded` Webhook が非同期で行うため、
 * confirmSetup 成功直後に画面上のカード情報を再取得しても旧カードが返る可能性がある。
 * そのため「即時反映されない」ことをユーザーに伝える。
 */
export const SUCCESS_MESSAGE = 'カード情報を登録しました。反映まで時間がかかる場合があります。';

/**
 * confirmSetup に渡す請求先住所
 *
 * `PaymentElement` の `fields.billingDetails.address` で `'never'` を指定したフィールドは、
 * `confirmSetup` 時に自前で渡す義務が発生する。義務は「Stripe が実際に入力欄を描画したか」ではなく
 * **`fields` に `'never'` と指定したかどうか**で判定される（渡さないと `IntegrationError` になる）。
 *
 * - `country`: PaymentMethod に付く請求先住所の国。ここで渡す国は使えるカードの発行国を制限しない
 * - `postal_code`: 収集しない（Figma に入力欄がない）ため `null` を渡して未収集を明示する
 *
 * キー名は confirmSetup にそのまま渡せるよう Stripe API の snake_case に合わせ、`satisfies` で
 * 型を固定している。`Address` は全プロパティが optional なため、`postalCode` のように綴りを
 * 間違えても型エラーにならず実行時の `IntegrationError` になる。それを compile 時に落とすため。
 *
 * @see https://docs.stripe.com/js/setup_intents/confirm_setup
 */
export const BILLING_ADDRESS = {
  country: 'JP',
  postal_code: null,
} as const satisfies PaymentMethodCreateParams.BillingDetails.Address;
